import "simplebar/src/simplebar.css";
import "react-lazy-load-image-component/src/effects/blur.css";
import "react-lazy-load-image-component/src/effects/opacity.css";
import "react-lazy-load-image-component/src/effects/black-and-white.css";

import { HelmetProvider } from "react-helmet-async";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { SettingsProvider } from "./contexts/SettingsContext";
import { CollapseDrawerProvider } from "./contexts/CollapseDrawerContext";

import React from "react";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { LicenseInfo } from "@mui/x-data-grid-premium";
import { AuthProvider } from "./contexts/BxAuthContext";
import { muiPremiumGridLicense } from "./config";
import { createRoot } from "react-dom/client";
import "./translations"
import {
    BrowserRouter,
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from "react-router-dom";
import useAuth from "./hooks/useAuth";

Sentry.init({
    dsn: "https://40f3f14feb834273bd2aa3a9893954ce@o388759.ingest.sentry.io/6624748",
    integrations: [
        new BrowserTracing({
            routingInstrumentation: Sentry.reactRouterV6Instrumentation(
                React.useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            ),
        }),
    ],

    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 1.0,
});

LicenseInfo.setLicenseKey(
    muiPremiumGridLicense
);

const SentryUser = () => {
    const { isAuthenticated, user, companyCode } = useAuth();

    if (isAuthenticated && user) {
        Sentry.setUser({
            id: `${user.userid} @ ${companyCode}`,
            username: `${user.username} @ ${companyCode}`,
        });

        Sentry.setTag("companyCode", companyCode);
        Sentry.setTag("residence", user.residence);
        Sentry.setTag("accesslevel", user.accesslevel);

        Sentry.setContext('profile', {
            companyCode: companyCode,
            username: user.username,
            fullName: user.fullname,
            companyName: user.companyname,
            clientProfile: user.profilename
        });

    }

    return null;
}

const root = createRoot(document.getElementById('root')!);

root.render(
    <React.StrictMode>
        <AuthProvider>
            <SentryUser/>
            <HelmetProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <SettingsProvider>
                        <CollapseDrawerProvider>
                            <BrowserRouter>
                                <App/>
                            </BrowserRouter>
                        </CollapseDrawerProvider>
                    </SettingsProvider>
                </LocalizationProvider>
            </HelmetProvider>
        </AuthProvider>
    </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
